import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload} from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import Paginate from '../../components/Paginate';
import {
  useGetHousesQuery,
  useDeleteHouseMutation,
  useCreateHouseMutation,
} from '../../slices/housesApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaWhatsapp } from 'react-icons/fa';
    


const HouseListScreen = () => {
  const { pageNumber } = useParams();
  const { data, isLoading, error, refetch } = useGetHousesQuery({ pageNumber });
  const [deleteHouse, { isLoading: loadingDelete }] = useDeleteHouseMutation();
  const [createHouse, { isLoading: loadingCreate }] = useCreateHouseMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' for ascending, 'desc' for descending

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure')) {
      try {
        await deleteHouse(id);
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const createHouseHandler = async () => {
    if (window.confirm('Are you sure you want to create a new house?')) {
      try {
        await createHouse();
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const filterAndSortHouses = () => {
    let filteredHouses = data.houses;

    // Filter houses by name, agent, type, or address
    if (filter) {
      filteredHouses = filteredHouses.filter(
        (house) =>
          house.name?.toLowerCase().includes(filter.toLowerCase()) ||
          house.agent?.toLowerCase().includes(filter.toLowerCase()) ||
          house.type?.toLowerCase().includes(filter.toLowerCase()) ||
          house.size?.toLowerCase().includes(filter.toLowerCase()) ||
          house.address?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Sort houses
    if (sortField) {
      filteredHouses.sort((a, b) => {
        if (sortField === 'regularPrice' || sortField === 'discountedPrice') {
          return sortOrder === 'asc'
            ? a[sortField] - b[sortField]
            : b[sortField] - a[sortField];
        } else {
          return sortOrder === 'asc'
            ? a[sortField].localeCompare(b[sortField])
            : b[sortField].localeCompare(a[sortField]);
        }
      });
    }

    return filteredHouses;
  };

  const downloadHouseImages = async (house) => {
      const zip = new JSZip();
      const folder = zip.folder(`House_${house._id}`);
  
      const imagePromises = house.images.map(async (imagePath, index) => {
        try {
          const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
          const blob = await response.blob();
          const urlParts = imagePath.split('/');
          const filename = urlParts[urlParts.length - 1]; // Use original filename
  
          folder.file(filename, blob);
        } catch (error) {
          console.error(`Failed to fetch ${imagePath}`, error);
        }
      });
  
      await Promise.all(imagePromises);
      zip.generateAsync({ type: 'blob' }).then((zipFile) => {
        saveAs(zipFile, `House_${house._id}_Images.zip`);
      });
    };
  
    const downloadAllImages = async () => {
      const zip = new JSZip();
      const folder = zip.folder('All_Houses_Images');
  
      const imagePromises = data.houses.flatMap((house) =>
        house.images.map(async (imagePath, index) => {
          try {
            const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
            const blob = await response.blob();
            const urlParts = imagePath.split('/');
            const filename = urlParts[urlParts.length - 1];
            folder.file(filename, blob);
            
          } catch (error) {
            console.error(`Failed to fetch ${imagePath}`, error);
          }
        })
      );
  
      await Promise.all(imagePromises);
      zip.generateAsync({ type: 'blob' }).then((zipFile) => {
        saveAs(zipFile, 'All_Houses_Images.zip');
      });
    };
  
     const exportToExcel = () => {
  if (!data || !data.houses || data.houses.length === 0) {
    toast.error("No houses available to export!");
    return;
  }

  const housesData = data.houses.map((house) => ({
    ID: house._id,
    Name: house.name || '',
    Agent: house.agent || '',
    Type: house.type || '',
    Address: house.address || '',
    RegularPrice: typeof house.regularPrice === 'number' ? house.regularPrice.toFixed(2) : '',
    DiscountedPrice: typeof house.discountedPrice === 'number' ? house.discountedPrice.toFixed(2) : '',
    Size: house.size || '',
    Availability: house.availability ? 'Available' : 'Unavailable',
    CreatedAt: house.createdAt ? new Date(house.createdAt).toLocaleDateString() : '',
  }));

  const worksheet = XLSX.utils.json_to_sheet(housesData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Houses');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const file = new Blob([excelBuffer], { type: 'application/octet-stream' });

  saveAs(file, 'Houses.xlsx');
};
    
    const shareHouseImages = (house) => {
  const baseUrl = window.location.origin;
  const imageLinks = house.images.map((img) => {
    const cleanedPath = img.replace(/\\/g, '/').replace(/^\/+/, ''); // Remove leading slashes
    return `${baseUrl}/${cleanedPath}`;
  });

  const message = `Check out this house: ${house.name}\nMoreInfo: ${house.size}\n\nImages:\n${imageLinks.join('\n')}`;
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;

  window.open(whatsappUrl, '_blank');
};

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Houses</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3 mx-2" onClick={downloadAllImages}>
            <FaDownload /> Download All Images
          </Button>
          <Button className="my-3 mx-2" onClick={exportToExcel}>
          <FaDownload /> Export to Excel
          </Button>
          <Button className="my-3" onClick={createHouseHandler}>
            <FaPlus /> Create House
          </Button>
          <Link to='/admin/bulkhousecreatescreen' className='btn btn-light my-3'>
          BulkCreate
        </Link>
        <Link to='/admin/bulkhouseeditscreen' className='btn btn-light my-3'>
          BulkUpdate
        </Link>
        </Col>
        
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by name, agent, type, or address"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error.data.message}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('name')}
                  style={{ cursor: 'pointer' }}
                >
                  NAME {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('type')}
                  style={{ cursor: 'pointer' }}
                >
                  TYPE {sortField === 'type' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('agent')}
                  style={{ cursor: 'pointer' }}
                >
                  AGENT {sortField === 'agent' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('address')}
                  style={{ cursor: 'pointer' }}
                >
                  ADDRESS {sortField === 'address' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th
                  onClick={() => handleSort('regularPrice')}
                  style={{ cursor: 'pointer' }}
                >
                  PRICE {sortField === 'regularPrice' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('size')}
                  style={{ cursor: 'pointer' }}
                >
                  More Details {sortField === 'size' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortHouses().map((house) => (
                <tr key={house._id}>
                  <td>{house.name}</td>
                  <td>{house.type}</td>
                  <td>{house.agent}</td>
                  <td>{house.address}</td>
                  
                  <td>${house.regularPrice.toFixed(2)}</td>
                  <td>{house.size}</td>
                  <td>
                    <Button
                      as={Link}
                      to={`/admin/house/${house._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button variant="info" className="btn-sm" onClick={() => downloadHouseImages(house)}>
                      <FaDownload />Images
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(house._id)}
                    >
                      <FaTrash style={{ color: 'white' }} />
                    </Button>
                    

                     <Button
                     variant="success"
                    className="btn-sm mx-2"
                     onClick={() => shareHouseImages(house)}
                      title="Share on WhatsApp"
                      >
                      <FaWhatsapp /> Share
                      </Button>
                    
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="houselist" />
        </>
      )}
    </>
  );
};

export default HouseListScreen;